// Department steps for a list of productions (Home cards, the Recent list, Productions): their summaries, and
// the steps of the latest episode as the server answers them, all from GET /production-summaries.
import { useEffect, useState } from "react";
import type { Api } from "../api.ts";
import type { Project } from "../types.ts";
import { type Summary, summaryReads } from "./production.ts";
import { useSources } from "./sources.ts";
import { type DeptStep, deriveSteps } from "./steps.ts";

export interface ProductionState {
  summary: Summary;
  steps: DeptStep[];
}

export function useProductionStates(
  api: Api,
  ws: string,
  projects: readonly Project[],
): Map<string, ProductionState> {
  const sources = useSources();
  const [states, setStates] = useState<Map<string, ProductionState>>(new Map());
  const key = projects.map((p) => p.id).join(",");
  // biome-ignore lint/correctness/useExhaustiveDependencies: the id list stands for the projects
  useEffect(() => {
    let live = true;
    for (const read of summaryReads(api, ws, projects)) {
      void (async () => {
        try {
          const loaded = await read;
          if (!loaded) return;
          const ep = loaded.steps ? await sources.steps.episodeFrom(loaded.steps).catch(() => null) : null;
          const { summary } = loaded;
          const steps = deriveSteps(summary.facts, ep?.signoffs, { gate: ep?.gate });
          if (live) setStates((m) => new Map(m).set(summary.project.id, { summary, steps }));
        } catch {
          // a production that does not load keeps its loading look; the screen shows the rest
        }
      })();
    }
    return () => {
      live = false;
    };
  }, [api, ws, key, sources]);
  return states;
}
